import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  LockKeyhole,
  DraftingCompass,
  FileText,
  Hammer,
  HardHat,
  Leaf,
  Mail,
  MapPin,
  Menu,
  Paintbrush,
  Phone,
  RefreshCw,
  Ruler,
  Send,
  ShieldCheck,
  Sparkles,
  Truck,
  UsersRound,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const company = {
  name: "DECTA Engineering and Construction",
  legalName: "Decta Engineering (PVT) LTD",
  tagline: "Whatever your vision, we bring it to your fingertips",
  phone: "+94 77 027 0205",
  secondaryPhone: "+94 75 544 3887",
  whatsapp: "+94 77 027 0205",
  email: "dectaengineeringpvt@gmail.com",
  location: "Mathakal Road, Pandatharippu, Jaffna, Sri Lanka 40000",
};

const images = {
  logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/6cccl2vj_IMG-20260723-WA0001.jpg",
  hero: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/hr3cihlq_FB_IMG_1784798115206.jpg",
  commercial: "https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  residential: "https://images.pexels.com/photos/26838218/pexels-photo-26838218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  structural: "https://images.unsplash.com/photo-1527335988388-b40ee248d80c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  blueprints: "https://images.pexels.com/photos/29502895/pexels-photo-29502895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  renovation: "https://images.unsplash.com/photo-1638885930125-85350348d266?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
  engineer: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/0b7e2utq_FB_IMG_1784798787412.webp",
  referenceBoard: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/hr3cihlq_FB_IMG_1784798115206.jpg",
  projectEarthwork1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/l0x5x1hm_FB_IMG_1784858174031.jpg",
  projectEarthwork2: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/4kr0z1j5_FB_IMG_1784858187545.jpg",
  projectEarthwork3: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/tui7tfg9_FB_IMG_1784858191988.jpg",
  projectEarthwork4: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/t92s09qq_FB_IMG_1784858197529.jpg",
  projectInterior1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/m6bjf8z4_FB_IMG_1784858202778.jpg",
  projectInterior2: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/1lbilcrj_FB_IMG_1784858148291.jpg",
  projectExterior1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/vv8qcru4_FB_IMG_1784858153128.jpg",
  projectExterior2: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/ib006pcp_FB_IMG_1784858162446.jpg",
  projectFlooring1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/iik642vj_FB_IMG_1784858166004.jpg",
  projectFoundation1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/fsuz0c2o_FB_IMG_1784858170563.jpg",
  projectFlooring2: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/5yok72w5_FB_IMG_1784858103102.jpg",
  projectStructure1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/buw6tqdu_FB_IMG_1784858106566.jpg",
  projectStructure2: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/pevrdq7c_FB_IMG_1784858112053.jpg",
  projectStructure3: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/wozpds7n_FB_IMG_1784858116403.jpg",
  projectStructure4: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/g5h3k7cr_FB_IMG_1784858135635.jpg",
  projectResidenceFront1: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/iftcv98h_FB_IMG_1784857950585.jpg",
  projectResidenceFront2: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/aegr0yk5_FB_IMG_1784857946433.jpg",
  projectResidenceExterior: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/3qqhjwkp_FB_IMG_1784857942510.jpg",
  projectResidencePorch: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/f9ede5e4_FB_IMG_1784857930161.jpg",
  projectResidenceCeiling: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/smeq76r6_FB_IMG_1784857930161.jpg",
  projectWoodDoor: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/gbp46zx9_FB_IMG_1784857976467.jpg",
  projectWoodCabinet: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/or09f2ww_FB_IMG_1784857970893.jpg",
  projectBlueFeatureWall: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/ebmda03e_FB_IMG_1784857966361.jpg",
  projectCeilingFeature: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/ex35jasp_FB_IMG_1784857961807.jpg",
  projectTvWallFinish: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/r3pr42bo_FB_IMG_1784857956094.jpg",
  projectPrayerRoom: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/a7gu29un_FB_IMG_1784858001460.jpg",
  projectElectricalSwitch: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/dbvxl03c_FB_IMG_1784857996485.jpg",
  projectKitchenWide: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/wqm4gnvu_FB_IMG_1784857992737.jpg",
  projectKitchenLow: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/nkfqunxz_FB_IMG_1784857987811.jpg",
  projectWallLight: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/cs4r3832_FB_IMG_1784857980272.jpg",
  projectVerandaColumns: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/xfc3x828_FB_IMG_1784858014526.jpg",
  projectBathroomSink: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/xo2hcru9_FB_IMG_1784858024921.jpg",
  projectBathroomToilet: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/ahp1em2m_FB_IMG_1784858028981.jpg",
  projectBathroomMirror: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/x5hna8ex_FB_IMG_1784858033879.jpg",
  projectBathroomHallway: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/axvqx8an_FB_IMG_1784858037980.jpg",
  projectBathroomBlueTile: "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/hpg6xk27_FB_IMG_1784858019304.jpg",
};

const aboutParagraphs = [
  "DECTA Engineering (Pvt) Ltd is a trusted construction and engineering company dedicated to delivering high-quality, reliable, and cost-effective solutions across Sri Lanka. With a team of highly qualified engineers, skilled technicians, and experienced construction professionals, we are committed to turning our clients' visions into reality.",
  "Our workforce brings years of industry experience and technical expertise to every project, ensuring the highest standards of quality, safety, and customer satisfaction. From planning and design to construction and project completion, we provide professional services tailored to each client's needs.",
  "At DECTA Engineering (Pvt) Ltd, we believe in building lasting relationships through honesty, quality workmanship, and timely project delivery. Whatever your vision, we bring it to your fingertips.",
];

const aboutSpecialties = [
  "Residential and commercial construction",
  "Renovation and remodeling",
  "2D & 3D architectural drawings",
  "BOQ (Bill of Quantities) preparation",
  "Landscaping",
  "Heavy equipment services",
  "Plumbing",
  "Painting",
  "Electrical works",
  "Skilled manpower services",
];

const navItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Projects", path: "/projects" },
  { label: "News", path: "/news" },
  { label: "Contact", path: "/contact" },
];

const services = [
  {
    title: "Construction",
    text: "Complete residential and commercial construction with planning, material coordination, supervision, and handover support.",
    icon: Building2,
    image: images.commercial,
  },
  {
    title: "Renovation Work",
    text: "Modern upgrades, repairs, extensions, and remodeling work for homes, shops, offices, and existing buildings.",
    icon: Hammer,
    image: images.renovation,
  },
  {
    title: "2D & 3D Drawings",
    text: "Clear architectural concepts, 2D plans, 3D visuals, and drawing support before starting construction.",
    icon: DraftingCompass,
    image: images.residential,
  },
  { title: "BOQ Preparation", text: "Detailed bill of quantities to understand material needs, labour scope, and project budget planning.", icon: FileText, image: images.blueprints },
  { title: "Landscaping", text: "Outdoor planning, garden improvements, pathways, boundary areas, and exterior finishing for complete properties.", icon: Leaf, image: images.hero },
  { title: "Heavy Equipment", text: "Support for site work requiring machinery coordination, earthwork preparation, and practical site execution.", icon: Truck, image: images.structural },
  { title: "Plumbing", text: "Water lines, drainage planning, bathroom/kitchen plumbing, and repair support during new builds or renovations.", icon: Wrench, image: images.renovation },
  { title: "Painting", text: "Interior and exterior painting support with clean finishing, surface preparation, and renovation touch-ups.", icon: Paintbrush, image: images.commercial },
  { title: "Electrical Work", text: "Electrical installation coordination, wiring support, lighting placement, and safe construction-stage planning.", icon: Zap, image: images.blueprints },
  { title: "Manpower Services", text: "Reliable construction manpower support for site work, renovation, finishing, and project delivery tasks.", icon: UsersRound, image: images.engineer },
];

const projects = [
  { title: "Completed Residence Finish", type: "Residential", image: images.projectResidenceFront1, size: "large", detail: "Finished home frontage, column detailing, ceiling design, flooring, and exterior completion." },
  { title: "Luxury Exterior & Roof Work", type: "Residential", image: images.projectResidenceExterior, size: "wide", detail: "Tile roof, veranda columns, front elevation, and completed residential exterior work." },
  { title: "Site Clearing & Earthwork", type: "Heavy Equipment", image: images.projectEarthwork1, size: "large", detail: "Land clearing, soil preparation, debris movement, and equipment-supported site readiness." },
  { title: "Excavator Ground Preparation", type: "Construction", image: images.projectEarthwork2, size: "small", detail: "Practical heavy machinery support for construction access and ground leveling." },
  { title: "Demolition & Debris Removal", type: "Site Services", image: images.projectEarthwork3, size: "small", detail: "Safe removal of old materials and site cleanup before new construction work." },
];

const galleryPhotos = [
  { title: "Blue tile bathroom finish", image: images.projectBathroomBlueTile, tag: "Bathroom" },
  { title: "Veranda column finishing", image: images.projectVerandaColumns, tag: "Exterior Finish" },
  { title: "Bathroom sink tile work", image: images.projectBathroomSink, tag: "Plumbing" },
  { title: "Modern toilet installation", image: images.projectBathroomToilet, tag: "Bathroom" },
  { title: "Bathroom mirror and wall tile", image: images.projectBathroomMirror, tag: "Tile Work" },
  { title: "Bathroom and hallway finish", image: images.projectBathroomHallway, tag: "Interior Finish" },
  { title: "Prayer room wooden unit", image: images.projectPrayerRoom, tag: "Interior Woodwork" },
  { title: "Electrical switch installation", image: images.projectElectricalSwitch, tag: "Electrical" },
  { title: "Modern kitchen cabinet work", image: images.projectKitchenWide, tag: "Kitchen" },
  { title: "Kitchen floor and cabinet finish", image: images.projectKitchenLow, tag: "Kitchen Finish" },
  { title: "Decorative wall light fitting", image: images.projectWallLight, tag: "Electrical Finish" },
  { title: "Wooden main door finishing", image: images.projectWoodDoor, tag: "Woodwork" },
  { title: "Traditional wooden cabinet", image: images.projectWoodCabinet, tag: "Carpentry" },
  { title: "Blue feature wall design", image: images.projectBlueFeatureWall, tag: "Interior" },
  { title: "Modern ceiling lighting", image: images.projectCeilingFeature, tag: "Ceiling Work" },
  { title: "TV wall interior finish", image: images.projectTvWallFinish, tag: "Finishing" },
  { title: "Completed residence front view", image: images.projectResidenceFront1, tag: "Completed Home" },
  { title: "Residence entrance and columns", image: images.projectResidenceFront2, tag: "Finishing" },
  { title: "Exterior roof and veranda", image: images.projectResidenceExterior, tag: "Residential" },
  { title: "Porch ceiling and flooring", image: images.projectResidencePorch, tag: "Interior Finish" },
  { title: "Decorative ceiling lighting", image: images.projectResidenceCeiling, tag: "Ceiling Work" },
  { title: "Blockwork structure progress", image: images.projectStructure1, tag: "Construction" },
  { title: "Roof frame construction", image: images.projectStructure2, tag: "Structure" },
  { title: "Residential wall construction", image: images.projectStructure3, tag: "Blockwork" },
  { title: "Front elevation work", image: images.projectStructure4, tag: "Site Progress" },
  { title: "Interior wall preparation", image: images.projectInterior2, tag: "Interior" },
  { title: "Exterior plastering work", image: images.projectExterior1, tag: "Plastering" },
  { title: "Wall finishing progress", image: images.projectExterior2, tag: "Finishing" },
  { title: "Floor concrete and leveling", image: images.projectFlooring1, tag: "Flooring" },
  { title: "Foundation column preparation", image: images.projectFoundation1, tag: "Foundation" },
  { title: "Heavy equipment site clearing", image: images.projectEarthwork1, tag: "Earthwork" },
  { title: "JCB ground preparation", image: images.projectEarthwork2, tag: "Equipment" },
  { title: "Construction access clearing", image: images.projectEarthwork3, tag: "Site Work" },
  { title: "Debris and soil removal", image: images.projectEarthwork4, tag: "Clearing" },
  { title: "Interior construction manpower", image: images.projectInterior1, tag: "Manpower" },
];

const newsVideo = "https://customer-assets-rejwkqb3.emergentagent.net/job_budget-monthly-24/artifacts/5x0p5250_km_20260725_1080p_30f_20260725_211027.mp4";

const news = [
  { title: "Understanding Sri Lanka Building Regulations", date: "Jul 25, 2026", tag: "Daily Update", summary: "A practical construction update for clients planning residential or commercial building work in Sri Lanka, covering why approvals, planning, and compliance matter before starting a project.", video: newsVideo },
  { title: "Why BOQ preparation helps control building cost", date: "Jan 18, 2026", tag: "BOQ", summary: "A clear quantity list helps owners compare prices, plan materials, and reduce surprise expenses." },
  { title: "2D and 3D drawings before starting construction", date: "Jan 11, 2026", tag: "Drawings", summary: "Drawings help families understand room layout, exterior design, and construction flow before work begins." },
  { title: "Renovation planning for homes and business places", date: "Jan 03, 2026", tag: "Renovation", summary: "Good planning for plumbing, painting, electrical work, and manpower can reduce delays on site." },
];

const stats = [
  { value: "10", label: "Core services" },
  { value: "2D/3D", label: "Drawing support" },
  { value: "Jaffna", label: "Local service area" },
];

const whatsappHref = (phone) => {
  const number = phone.replace(/\D/g, "");
  const message = encodeURIComponent("Hello DECTA Construction Engineering, I want to discuss a construction project.");
  return `https://wa.me/${number}?text=${message}`;
};

const googleMapsHref = (address) => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const siteUrl = "https://dectaconstruction.com";

const newsShareText = (item) => `${item.title} - ${company.name}`;

const shareLinks = (item) => {
  const url = `${siteUrl}/news`;
  const text = newsShareText(item);
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    instagram: "https://www.instagram.com/",
  };
};

const shareToDevice = async (item) => {
  const url = `${siteUrl}/news`;
  const text = newsShareText(item);
  try {
    if (navigator.share) {
      await navigator.share({ title: item.title, text, url });
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast.success("News link copied for sharing");
  } catch (error) {
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast.success("News link copied for sharing");
  }
};

const glowmaxPhone = "+44 7405 209034";

const glowmaxWhatsAppHref = () => {
  const number = glowmaxPhone.replace(/\D/g, "");
  const message = encodeURIComponent("Hello GlowmaxIT, I am interested in web design or app design services.");
  return `https://wa.me/${number}?text=${message}`;
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

function PageShell({ children }) {
  return (
    <main data-testid="page-shell" className="grain-layer min-h-screen overflow-hidden bg-[#0A0B0E] text-[#F3F4F6]">
      {children}
      <FloatingWhatsApp />
    </main>
  );
}

function FloatingWhatsApp() {
  return (
    <a
      data-testid="floating-whatsapp-link"
      href={whatsappHref(company.phone)}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-5 right-5 z-50 hidden items-center justify-center border border-[#25D366]/70 bg-[#25D366] px-5 py-4 text-[#0A0B0E] shadow-[0_18px_45px_rgba(37,211,102,0.28)] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#39E77A] sm:flex"
      aria-label="Contact DECTA on WhatsApp"
    >
      <Phone data-testid="floating-whatsapp-icon" className="mr-2 h-5 w-5" />
      <span data-testid="floating-whatsapp-text" className="text-sm font-bold uppercase tracking-[0.12em]">WhatsApp</span>
    </a>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="site-header" className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-[rgba(10,11,14,0.93)] shadow-[0_18px_45px_rgba(0,0,0,0.28)] backdrop-blur-2xl">
      <nav data-testid="site-navigation" className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link data-testid="brand-home-link" to="/" className="group flex items-center gap-3">
          <span data-testid="brand-mark" className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden border border-[#C5A059]/60 bg-white p-1 transition-[transform,border-color] duration-300 group-hover:-translate-y-0.5 group-hover:border-[#DFB668]">
            <img data-testid="brand-logo-image" src={images.logo} alt="DECTA Engineering logo" className="h-full w-full object-contain" />
          </span>
          <span className="leading-none">
            <span data-testid="brand-name" className="block font-display text-lg font-semibold uppercase text-[#F3F4F6] sm:text-xl">{company.name}</span>
            <span data-testid="brand-tagline" className="hidden text-xs uppercase tracking-[0.28em] text-[#8E95A3] sm:block">{company.tagline}</span>
          </span>
        </Link>
        <div data-testid="desktop-nav-links" className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${isActive ? "text-[#C5A059]" : "text-[#8E95A3] hover:text-[#F3F4F6]"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <Link data-testid="header-quote-link" to="/contact" className="hidden lg:block">
          <Button data-testid="header-quote-button" className="rounded-none border border-[#C5A059] bg-[#C5A059] px-5 text-[#0A0B0E] transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1 hover:bg-[#DFB668]">
            Request Quote <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
        <button data-testid="mobile-menu-toggle" className="grid h-11 w-11 place-items-center border border-white/15 text-[#F3F4F6] lg:hidden" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>
      {open && (
        <div data-testid="mobile-nav-panel" className="border-t border-white/10 bg-[#0A0B0E] px-5 py-5 lg:hidden">
          {navItems.map((item) => (
            <NavLink data-testid={`mobile-nav-link-${item.label.toLowerCase()}`} onClick={() => setOpen(false)} key={item.path} to={item.path} className="block border-b border-white/10 py-4 text-sm uppercase tracking-[0.16em] text-[#F3F4F6]">
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}

function SectionIntro({ label, title, text }) {
  return (
    <div data-testid={`section-intro-${label.toLowerCase().replace(/\s+/g, "-")}`} className="max-w-3xl">
      <p data-testid={`section-label-${label.toLowerCase().replace(/\s+/g, "-")}`} className="mb-4 text-sm font-semibold uppercase tracking-[0.32em] text-[#C5A059]">{label}</p>
      <h2 data-testid={`section-title-${label.toLowerCase().replace(/\s+/g, "-")}`} className="max-w-full break-words font-display text-2xl font-black uppercase leading-tight text-[#F3F4F6] sm:text-5xl lg:text-6xl">{title}</h2>
      {text && <p data-testid={`section-text-${label.toLowerCase().replace(/\s+/g, "-")}`} className="mt-6 max-w-2xl text-base leading-8 text-[#B7BDCA] md:text-lg">{text}</p>}
    </div>
  );
}

function Hero() {
  return (
    <section data-testid="home-hero-section" className="relative overflow-hidden pt-20 lg:min-h-[92vh]">
      <div data-testid="home-hero-mobile-image-wrap" className="relative bg-[#0A0B0E] lg:hidden">
        <img data-testid="home-hero-mobile-image" src={images.hero} alt="DECTA Engineering service banner" className="h-auto w-full object-contain" />
      </div>
      <img data-testid="home-hero-image" src={images.hero} alt="DECTA Engineering service banner" className="absolute inset-0 hidden h-full w-full object-cover object-center lg:block" />
      <div className="absolute inset-0 hidden bg-[#0A0B0E]/55 lg:block" />
      <div className="absolute inset-0 hidden blueprint-grid opacity-55 lg:block" />
      <div className="relative z-10 mx-auto flex max-w-7xl flex-col justify-end px-5 pb-16 pt-10 sm:px-8 lg:min-h-[calc(92vh-5rem)] lg:pb-20 lg:pt-24">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }} className="max-w-5xl">
          <p data-testid="hero-eyebrow" className="mb-5 text-sm font-semibold uppercase tracking-[0.32em] text-[#DFB668]">Precision built. Structurally led.</p>
          <h1 data-testid="hero-title" className="max-w-full break-words font-display text-[2.55rem] font-medium uppercase leading-[1.05] text-[#F3F4F6] sm:text-6xl lg:text-7xl lg:leading-[0.98] xl:text-8xl">
            Engineering & Construction
          </h1>
          <p data-testid="hero-description" className="mt-7 max-w-2xl text-base leading-8 text-[#D7DBE4] md:text-lg">
            {company.legalName} supports construction, renovation work, 2D/3D drawings, BOQ preparation, landscaping, heavy equipment, plumbing, painting, electrical work, and manpower services in Jaffna.
          </p>
          <div data-testid="hero-actions" className="mt-9 flex flex-col gap-4 sm:flex-row">
            <a data-testid="hero-whatsapp-link" href={whatsappHref(company.phone)} target="_blank" rel="noopener noreferrer">
              <Button data-testid="hero-whatsapp-button" className="h-12 w-full rounded-none bg-[#25D366] px-7 text-[#0A0B0E] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#39E77A] sm:w-auto">
                WhatsApp Now <Phone className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link data-testid="hero-contact-link" to="/contact">
              <Button data-testid="hero-contact-button" className="h-12 w-full rounded-none bg-[#C5A059] px-7 text-[#0A0B0E] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#DFB668] sm:w-auto">
                Start Your Project <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link data-testid="hero-projects-link" to="/projects">
              <Button data-testid="hero-projects-button" variant="outline" className="h-12 rounded-none border-white/25 bg-black/20 px-7 text-[#F3F4F6] transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#C5A059] hover:bg-white/10">
                View Projects
              </Button>
            </Link>
          </div>
        </motion.div>
        <div data-testid="hero-stats" className="mt-12 grid max-w-4xl grid-cols-1 border border-white/15 bg-black/35 backdrop-blur-xl sm:grid-cols-3">
          {stats.map((stat) => (
            <div data-testid={`hero-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`} key={stat.label} className="border-b border-white/15 p-5 sm:border-b-0 sm:border-r last:border-r-0">
              <p className="font-display text-3xl text-[#C5A059]">{stat.value}</p>
              <p className="mt-2 text-sm uppercase tracking-[0.14em] text-[#B7BDCA]">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesGrid({ compact = false }) {
  return (
    <div data-testid="services-grid" className="grid border border-[#2A2F3D] md:grid-cols-2">
      {services.map((service, index) => {
        const Icon = service.icon;
        return (
          <motion.article
            data-testid={`service-card-${index}`}
            key={service.title}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            transition={{ delay: index * 0.08, duration: 0.55 }}
            className="group border-b border-[#2A2F3D] bg-[#12141A]/65 p-6 transition-[background-color,border-color] duration-300 hover:border-[#C5A059]/60 hover:bg-[#151923] md:border-r md:p-8 even:md:border-r-0"
          >
            {!compact && <img data-testid={`service-image-${index}`} src={service.image} alt={service.title} className="mb-7 aspect-[16/9] w-full object-cover grayscale transition-[filter,transform] duration-500 group-hover:grayscale-0 group-hover:scale-[1.015]" />}
            <div data-testid={`service-icon-${index}`} className="mb-6 grid h-12 w-12 place-items-center border border-[#C5A059]/50 text-[#C5A059]"><Icon size={24} /></div>
            <h3 data-testid={`service-title-${index}`} className="font-display text-2xl font-semibold uppercase text-[#F3F4F6]">{service.title}</h3>
            <p data-testid={`service-text-${index}`} className="mt-4 text-base leading-7 text-[#B7BDCA]">{service.text}</p>
          </motion.article>
        );
      })}
    </div>
  );
}

function ProjectsGrid() {
  return (
    <div data-testid="projects-grid" className="grid auto-rows-[270px] gap-4 md:grid-cols-4">
      {projects.map((project, index) => (
        <motion.article
          data-testid={`project-card-${index}`}
          key={project.title}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          transition={{ delay: index * 0.08, duration: 0.55 }}
          className={`group relative overflow-hidden border border-white/10 bg-[#12141A] ${project.size === "large" ? "md:col-span-2 md:row-span-2" : project.size === "wide" ? "md:col-span-2" : "md:col-span-1"}`}
        >
          <img data-testid={`project-image-${index}`} src={project.image} alt={project.title} className="absolute inset-0 h-full w-full object-cover grayscale transition-[filter,transform] duration-700 group-hover:scale-105 group-hover:grayscale-0" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/45 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <p data-testid={`project-type-${index}`} className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#C5A059]">{project.type}</p>
            <h3 data-testid={`project-title-${index}`} className="font-display text-2xl font-semibold uppercase text-[#F3F4F6]">{project.title}</h3>
            <p data-testid={`project-detail-${index}`} className="mt-3 max-w-xl text-sm leading-6 text-[#D7DBE4]">{project.detail}</p>
          </div>
        </motion.article>
      ))}
    </div>
  );
}

function GalleryGrid() {
  return (
    <div data-testid="project-gallery-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {galleryPhotos.map((photo, index) => (
        <article data-testid={`gallery-card-${index}`} key={photo.title} className="group border border-[#2A2F3D] bg-[#12141A] p-3 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#C5A059]/70 hover:bg-[#151923]">
          <div data-testid={`gallery-image-wrap-${index}`} className="grid aspect-[4/3] place-items-center overflow-hidden bg-[#0A0B0E]">
            <img data-testid={`gallery-image-${index}`} src={photo.image} alt={photo.title} className="h-full w-full object-contain transition-[transform] duration-500 group-hover:scale-[1.02]" />
          </div>
          <div className="p-4">
            <p data-testid={`gallery-tag-${index}`} className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#C5A059]">{photo.tag}</p>
            <h3 data-testid={`gallery-title-${index}`} className="font-display text-xl font-semibold uppercase text-[#F3F4F6]">{photo.title}</h3>
          </div>
        </article>
      ))}
    </div>
  );
}

function NewsList() {
  return (
    <div data-testid="news-list" className="grid gap-4 lg:grid-cols-3">
      {news.map((item, index) => (
        <article data-testid={`news-card-${index}`} key={item.title} className={`border border-[#2A2F3D] bg-[#12141A] p-6 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#C5A059]/70 hover:bg-[#151923] ${item.video ? "lg:col-span-3" : ""}`}>
          {item.video && (
            <div data-testid={`news-video-wrap-${index}`} className="mb-6 overflow-hidden border border-[#2A2F3D] bg-[#0A0B0E]">
              <video data-testid={`news-video-${index}`} className="aspect-video w-full object-contain" src={item.video} controls preload="metadata" playsInline />
            </div>
          )}
          <div className="mb-8 flex items-center justify-between gap-4">
            <span data-testid={`news-tag-${index}`} className="text-xs font-semibold uppercase tracking-[0.24em] text-[#C5A059]">{item.tag}</span>
            <span data-testid={`news-date-${index}`} className="text-xs uppercase tracking-[0.16em] text-[#8E95A3]">{item.date}</span>
          </div>
          <h3 data-testid={`news-title-${index}`} className="font-display text-2xl font-semibold uppercase leading-tight text-[#F3F4F6]">{item.title}</h3>
          <p data-testid={`news-summary-${index}`} className="mt-5 text-base leading-7 text-[#B7BDCA]">{item.summary}</p>
          <SocialShare item={item} index={index} />
        </article>
      ))}
    </div>
  );
}

function SocialShare({ item, index }) {
  const links = shareLinks(item);
  return (
    <div data-testid={`news-share-row-${index}`} className="mt-6 flex flex-wrap gap-3 border-t border-[#2A2F3D] pt-5">
      <a data-testid={`news-share-facebook-${index}`} href={links.facebook} target="_blank" rel="noopener noreferrer" className="border border-[#2A2F3D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#D7DBE4] transition-[transform,border-color,color] duration-300 hover:-translate-y-1 hover:border-[#C5A059] hover:text-[#C5A059]">Facebook</a>
      <a data-testid={`news-share-whatsapp-${index}`} href={links.whatsapp} target="_blank" rel="noopener noreferrer" className="border border-[#2A2F3D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#D7DBE4] transition-[transform,border-color,color] duration-300 hover:-translate-y-1 hover:border-[#25D366] hover:text-[#25D366]">WhatsApp</a>
      <a data-testid={`news-share-instagram-${index}`} href={links.instagram} target="_blank" rel="noopener noreferrer" onClick={() => shareToDevice(item)} className="border border-[#2A2F3D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#D7DBE4] transition-[transform,border-color,color] duration-300 hover:-translate-y-1 hover:border-[#C5A059] hover:text-[#C5A059]">Instagram</a>
      <a data-testid={`news-share-linkedin-${index}`} href={links.linkedin} target="_blank" rel="noopener noreferrer" className="border border-[#2A2F3D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#D7DBE4] transition-[transform,border-color,color] duration-300 hover:-translate-y-1 hover:border-[#C5A059] hover:text-[#C5A059]">LinkedIn</a>
      <button data-testid={`news-share-more-${index}`} type="button" onClick={() => shareToDevice(item)} className="inline-flex items-center border border-[#2A2F3D] px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#D7DBE4] transition-[transform,border-color,color] duration-300 hover:-translate-y-1 hover:border-[#C5A059] hover:text-[#C5A059]"><Send className="mr-2 h-3.5 w-3.5" />Share</button>
    </div>
  );
}

function Home() {
  return (
    <PageShell>
      <Navbar />
      <Hero />
      <section data-testid="home-services-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="mb-12 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionIntro label="Our services" title="One local team for construction and site work" text="Inspired by the DECTA service board, this website now presents the full service list clearly for customers who need building work, drawings, renovation, and manpower support." />
          <Link data-testid="home-services-link" to="/services" className="text-sm font-semibold uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:text-[#DFB668]">All services</Link>
        </div>
        <ServicesGrid compact />
      </section>
      <section data-testid="reference-board-section" className="border-y border-[#2A2F3D] bg-[#0D0F13] px-5 py-24 sm:px-8 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1fr] lg:items-center">
          <div>
            <SectionIntro label="Local identity" title="Clear service board style, upgraded for web" text="The website keeps the practical local-business message from the reference photo while presenting it in a modern premium layout." />
            <div data-testid="reference-contact-strip" className="mt-8 grid gap-3 sm:grid-cols-2">
              <a data-testid="reference-call-primary" href={`tel:${company.phone}`} className="border border-[#C5A059]/50 bg-[#C5A059]/10 p-4 text-[#F3F4F6] transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#DFB668]"><Phone className="mb-3 text-[#C5A059]" />{company.phone}</a>
              <a data-testid="reference-call-secondary" href={`tel:${company.secondaryPhone}`} className="border border-[#C5A059]/50 bg-[#C5A059]/10 p-4 text-[#F3F4F6] transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#DFB668]"><Phone className="mb-3 text-[#C5A059]" />{company.secondaryPhone}</a>
            </div>
          </div>
          <div data-testid="reference-board-image-wrap" className="border border-[#2A2F3D] bg-[#12141A] p-3">
            <img data-testid="reference-board-image" src={images.referenceBoard} alt="DECTA Engineering reference service board" className="aspect-[4/3] w-full object-contain" />
          </div>
        </div>
      </section>
      <section data-testid="home-projects-section" className="border-y border-[#2A2F3D] bg-[#0D0F13] px-5 py-24 sm:px-8 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12"><SectionIntro label="Featured projects" title="Built with visible discipline" /></div>
          <ProjectsGrid />
        </div>
      </section>
      <section data-testid="home-news-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="mb-12"><SectionIntro label="Daily news" title="Latest update appears here first" text="Every new construction news post is shown on the front page and can be shared to Facebook, WhatsApp, Instagram, LinkedIn, and other social media." /></div>
        <NewsList />
      </section>
      <CTA />
      <Footer />
    </PageShell>
  );
}

function About() {
  return (
    <PageShell>
      <Navbar />
      <PageHero label="About Us" title="Trusted engineering and construction across Sri Lanka" image={images.engineer} />
      <section data-testid="about-mission-section" className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-[1fr_0.8fr] lg:py-32">
        <div>
          <SectionIntro label="About Us" title="Built on quality, safety, and reliable delivery" />
          <div data-testid="about-full-text" className="mt-8 space-y-6">
            {aboutParagraphs.map((paragraph, index) => (
              <p data-testid={`about-paragraph-${index}`} key={paragraph} className="text-base leading-8 text-[#B7BDCA] md:text-lg">
                {paragraph}
              </p>
            ))}
          </div>
          <div data-testid="about-specialties-panel" className="mt-10 border border-[#2A2F3D] bg-[#12141A] p-6">
            <h3 data-testid="about-specialties-title" className="font-display text-2xl font-semibold uppercase text-[#F3F4F6]">We specialize in</h3>
            <div data-testid="about-specialties-grid" className="mt-6 grid gap-3 sm:grid-cols-2">
              {aboutSpecialties.map((item, index) => (
                <div data-testid={`about-specialty-${index}`} key={item} className="flex gap-3 text-sm leading-6 text-[#D7DBE4]">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#C5A059]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="about-values-grid" className="mt-10 grid gap-4 sm:grid-cols-3">
            {["Honesty", "Quality workmanship", "Timely delivery"].map((value) => (
              <div data-testid={`about-value-${value.toLowerCase().replace(/\s+/g, "-")}`} key={value} className="border border-[#2A2F3D] p-5 text-[#D7DBE4]"><CheckCircle2 className="mb-4 text-[#C5A059]" />{value}</div>
            ))}
          </div>
        </div>
        <div data-testid="about-inspection-panel" className="border border-[#2A2F3D] bg-[#12141A] p-8">
          <img data-testid="about-engineer-photo" src={images.engineer} alt="DECTA engineer at construction site" className="mb-8 aspect-[4/5] w-full object-cover object-center" />
          <ClipboardCheck data-testid="about-inspection-icon" className="mb-8 h-12 w-12 text-[#C5A059]" />
          <h3 data-testid="about-inspection-title" className="font-display text-3xl uppercase text-[#F3F4F6]">How we work</h3>
          <p data-testid="about-inspection-text" className="mt-5 text-base leading-8 text-[#B7BDCA]">We listen to the customer vision, prepare drawing and BOQ support where needed, organize the right manpower, and coordinate construction or renovation work stage by stage.</p>
        </div>
      </section>
      <Footer />
    </PageShell>
  );
}

function Services() {
  return (
    <PageShell>
      <Navbar />
      <PageHero label="Services" title="Construction, renovation, drawings, BOQ, and site support" image={images.structural} />
      <section data-testid="services-page-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32"><ServicesGrid /></section>
      <Footer />
    </PageShell>
  );
}

function Projects() {
  return (
    <PageShell>
      <Navbar />
      <PageHero label="Projects" title="Residential finishing, renovation, and site service examples" image={images.projectResidenceFront1} />
      <section data-testid="projects-page-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="mb-12"><SectionIntro label="Project work" title="Recent residential and construction photos" text="Real project photos showing DECTA Engineering and Construction completed residence finishing, ceiling work, exterior work, heavy equipment, site clearing, renovation, and manpower services." /></div>
        <ProjectsGrid />
      </section>
      <section data-testid="gallery-page-section" className="border-t border-[#2A2F3D] bg-[#0D0F13] px-5 py-24 sm:px-8 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12"><SectionIntro label="Gallery" title="Project gallery" text="A clean gallery of uploaded project images for customers to view previous site work." /></div>
          <GalleryGrid />
        </div>
      </section>
      <Footer />
    </PageShell>
  );
}

function News() {
  return (
    <PageShell>
      <Navbar />
      <PageHero label="Latest news" title="Daily construction and regulation updates" image={images.blueprints} />
      <section data-testid="news-page-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32"><NewsList /></section>
      <Footer />
    </PageShell>
  );
}

function PageHero({ label, title, image }) {
  return (
    <section data-testid={`page-hero-${label.toLowerCase().replace(/\s+/g, "-")}`} className="relative min-h-[58vh] overflow-hidden pt-20">
      <img data-testid={`page-hero-image-${label.toLowerCase().replace(/\s+/g, "-")}`} src={image} alt={`${label} construction`} className="absolute inset-0 h-full w-full object-cover object-center grayscale" />
      <div className="absolute inset-0 bg-[#0A0B0E]/68" />
      <div className="absolute inset-0 blueprint-grid opacity-45" />
      <div className="relative z-10 mx-auto flex min-h-[calc(58vh-5rem)] max-w-7xl flex-col justify-end px-5 pb-14 sm:px-8">
        <p data-testid={`page-hero-label-${label.toLowerCase().replace(/\s+/g, "-")}`} className="mb-5 text-sm font-semibold uppercase tracking-[0.32em] text-[#C5A059]">{label}</p>
        <h1 data-testid={`page-hero-title-${label.toLowerCase().replace(/\s+/g, "-")}`} className="max-w-5xl break-words font-display text-[2.65rem] font-medium uppercase leading-tight text-[#F3F4F6] sm:text-6xl lg:text-7xl">{title}</h1>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "Construction", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry saved. Our team will contact you soon.");
      setForm({ name: "", email: "", phone: "", service: "Construction", message: "" });
    } catch (error) {
      toast.error("Please check the form and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <Navbar />
      <PageHero label="Contact" title="Tell us about your building project" image={images.renovation} />
      <section data-testid="contact-section" className="mx-auto grid max-w-7xl gap-10 px-5 py-24 sm:px-8 lg:grid-cols-[0.82fr_1fr] lg:py-32">
        <div data-testid="contact-details-panel" className="border border-[#2A2F3D] bg-[#12141A] p-7 sm:p-8">
          <p data-testid="contact-panel-label" className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-[#C5A059]">Direct contact</p>
          <h2 data-testid="contact-panel-title" className="font-display text-4xl font-black uppercase leading-tight text-[#F3F4F6]">Speak with an engineering project lead</h2>
          <div data-testid="contact-methods" className="mt-9 space-y-5">
            <ContactMethod icon={Phone} label="Phone" value={company.phone} href={`tel:${company.phone}`} testId="phone" />
            <ContactMethod icon={Phone} label="Second Phone" value={company.secondaryPhone} href={`tel:${company.secondaryPhone}`} testId="second-phone" />
            <ContactMethod icon={Phone} label="WhatsApp Primary" value={company.phone} href={whatsappHref(company.phone)} testId="whatsapp" external />
            <ContactMethod icon={Phone} label="WhatsApp Second Number" value={company.secondaryPhone} href={whatsappHref(company.secondaryPhone)} testId="whatsapp-second" external />
            <ContactMethod icon={Mail} label="Email" value={company.email} href={`mailto:${company.email}`} testId="email" />
            <ContactMethod icon={MapPin} label="Location" value={company.location} href={googleMapsHref(company.location)} testId="location" external />
          </div>
        </div>
        <form data-testid="contact-enquiry-form" onSubmit={submit} className="border border-[#2A2F3D] bg-[#0D0F13] p-7 sm:p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Name" id="name"><Input data-testid="contact-name-input" id="name" value={form.name} onChange={(e) => update("name", e.target.value)} required minLength={2} className="rounded-none border-[#2A2F3D] bg-[#12141A] text-[#F3F4F6]" /></Field>
            <Field label="Phone" id="phone"><Input data-testid="contact-phone-input" id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} required minLength={6} className="rounded-none border-[#2A2F3D] bg-[#12141A] text-[#F3F4F6]" /></Field>
            <Field label="Email" id="email"><Input data-testid="contact-email-input" id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="rounded-none border-[#2A2F3D] bg-[#12141A] text-[#F3F4F6]" /></Field>
            <Field label="Service" id="service">
              <select data-testid="contact-service-select" id="service" value={form.service} onChange={(e) => update("service", e.target.value)} className="h-10 w-full rounded-none border border-[#2A2F3D] bg-[#12141A] px-3 text-sm text-[#F3F4F6] outline-none focus:border-[#C5A059]">
                {services.map((service) => <option data-testid={`service-option-${service.title.toLowerCase().replace(/\s+|&/g, "-")}`} key={service.title}>{service.title}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-6"><Field label="Project message" id="message"><Textarea data-testid="contact-message-textarea" id="message" value={form.message} onChange={(e) => update("message", e.target.value)} required minLength={10} className="min-h-36 rounded-none border-[#2A2F3D] bg-[#12141A] text-[#F3F4F6]" /></Field></div>
          <Button data-testid="contact-submit-button" disabled={loading} className="mt-7 h-12 rounded-none bg-[#C5A059] px-8 text-[#0A0B0E] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#DFB668] disabled:opacity-70">
            {loading ? "Saving enquiry..." : "Send Enquiry"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </form>
      </section>
      <Toaster richColors position="top-right" />
      <Footer />
    </PageShell>
  );
}

function Field({ label, id, children }) {
  return <div data-testid={`field-${id}`}><Label data-testid={`label-${id}`} htmlFor={id} className="mb-2 block text-sm uppercase tracking-[0.14em] text-[#B7BDCA]">{label}</Label>{children}</div>;
}

function ContactMethod({ icon: Icon, label, value, href, testId, external = false }) {
  const content = (
    <div data-testid={`contact-method-${testId}`} className="flex gap-4 border border-[#2A2F3D] p-4 transition-[border-color,background-color] duration-300 hover:border-[#C5A059]/60 hover:bg-white/[0.03]">
      <Icon data-testid={`contact-method-icon-${testId}`} className="mt-1 h-5 w-5 shrink-0 text-[#C5A059]" />
      <div>
        <p data-testid={`contact-method-label-${testId}`} className="text-xs uppercase tracking-[0.2em] text-[#8E95A3]">{label}</p>
        <p data-testid={`contact-method-value-${testId}`} className="mt-1 break-words text-base text-[#F3F4F6]">{value}</p>
      </div>
    </div>
  );
  return href ? <a data-testid={`contact-method-link-${testId}`} href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>{content}</a> : content;
}

function CTA() {
  return (
    <section data-testid="cta-section" className="border-y border-[#2A2F3D] bg-[#12141A] px-5 py-20 sm:px-8">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 lg:flex-row lg:items-center">
        <div>
          <p data-testid="cta-label" className="mb-3 text-sm font-semibold uppercase tracking-[0.28em] text-[#C5A059]">Project consultation</p>
          <h2 data-testid="cta-title" className="font-display text-4xl font-black uppercase text-[#F3F4F6] sm:text-5xl">Ready to start your construction work?</h2>
        </div>
        <Link data-testid="cta-contact-link" to="/contact"><Button data-testid="cta-contact-button" className="h-12 rounded-none bg-[#C5A059] px-7 text-[#0A0B0E] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#DFB668]">Request Estimate <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-[#2A2F3D] bg-[#0A0B0E] px-5 py-10 sm:px-8">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-5 text-sm text-[#8E95A3] md:flex-row md:items-start">
        <div>
          <p data-testid="footer-company">© 2026 {company.name}. {company.legalName}.</p>
          <p data-testid="footer-services" className="mt-2">Construction · Renovation · 2D/3D Drawings · BOQ · Plumbing · Painting · Electrical</p>
        </div>
        <a
          data-testid="footer-glowmax-whatsapp-link"
          href={glowmaxWhatsAppHref()}
          target="_blank"
          rel="noopener noreferrer"
          className="max-w-xs border border-[#2A2F3D] bg-[#12141A] p-4 text-left transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-[#25D366]/70 hover:bg-[#151923]"
        >
          <span data-testid="footer-glowmax-name" className="block font-display text-base font-semibold uppercase text-[#F3F4F6]">glowmaxit</span>
          <span data-testid="footer-glowmax-service" className="mt-1 block text-xs uppercase tracking-[0.14em] text-[#C5A059]">Web design and Apps design</span>
          <span data-testid="footer-glowmax-phone" className="mt-2 flex items-center gap-2 text-sm text-[#B7BDCA]"><Phone className="h-4 w-4 text-[#25D366]" />{glowmaxPhone}</span>
        </a>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div data-testid="app-root" className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/news" element={<News />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function Admin() {
  const [login, setLogin] = useState({ email: "", password: "" });
  const [token, setToken] = useState(() => window.localStorage.getItem("dectaAdminToken") || "");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadEnquiries = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setEnquiries(response.data);
    } catch (err) {
      setError("Please login again to view enquiries.");
      window.localStorage.removeItem("dectaAdminToken");
      setToken("");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const submitLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, login);
      window.localStorage.setItem("dectaAdminToken", response.data.token);
      setToken(response.data.token);
      toast.success("Admin login successful");
    } catch (err) {
      setError("Wrong admin email or password.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Enquiry status updated");
      await loadEnquiries();
    } catch (err) {
      toast.error("Could not update enquiry");
    }
  };

  const logout = () => {
    window.localStorage.removeItem("dectaAdminToken");
    setToken("");
    setEnquiries([]);
  };

  useEffect(() => {
    if (token) {
      loadEnquiries();
    }
  }, [token, loadEnquiries]);

  return (
    <PageShell>
      <Navbar />
      <section data-testid="admin-page" className="mx-auto min-h-screen max-w-7xl px-5 pb-24 pt-32 sm:px-8">
        <div className="mb-10 flex flex-col justify-between gap-5 border-b border-[#2A2F3D] pb-8 lg:flex-row lg:items-end">
          <div>
            <p data-testid="admin-page-label" className="mb-4 text-sm font-semibold uppercase tracking-[0.32em] text-[#C5A059]">Hidden admin</p>
            <h1 data-testid="admin-page-title" className="font-display text-4xl font-black uppercase leading-tight text-[#F3F4F6] sm:text-5xl">Customer Enquiries</h1>
            <p data-testid="admin-page-description" className="mt-4 max-w-2xl text-base leading-7 text-[#B7BDCA]">Login to view customer messages submitted from the website contact form.</p>
          </div>
          {token && (
            <div className="flex gap-3">
              <Button data-testid="admin-refresh-button" onClick={loadEnquiries} className="rounded-none border border-[#2A2F3D] bg-[#12141A] text-[#F3F4F6] hover:bg-[#151923]"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
              <Button data-testid="admin-logout-button" onClick={logout} className="rounded-none bg-[#C5A059] text-[#0A0B0E] hover:bg-[#DFB668]">Logout</Button>
            </div>
          )}
        </div>

        {!token ? (
          <form data-testid="admin-login-form" onSubmit={submitLogin} className="max-w-xl border border-[#2A2F3D] bg-[#12141A] p-7 sm:p-8">
            <LockKeyhole data-testid="admin-login-icon" className="mb-6 h-10 w-10 text-[#C5A059]" />
            <div className="space-y-5">
              <Field label="Admin email" id="admin-email">
                <Input data-testid="admin-email-input" id="admin-email" type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} required className="rounded-none border-[#2A2F3D] bg-[#0A0B0E] text-[#F3F4F6]" />
              </Field>
              <Field label="Password" id="admin-password">
                <Input data-testid="admin-password-input" id="admin-password" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} required className="rounded-none border-[#2A2F3D] bg-[#0A0B0E] text-[#F3F4F6]" />
              </Field>
            </div>
            {error && <p data-testid="admin-login-error" className="mt-5 text-sm text-red-300">{error}</p>}
            <Button data-testid="admin-login-submit-button" disabled={loading} className="mt-7 h-12 w-full rounded-none bg-[#C5A059] text-[#0A0B0E] hover:bg-[#DFB668]">{loading ? "Logging in..." : "Login"}</Button>
          </form>
        ) : (
          <div data-testid="admin-enquiries-list" className="space-y-4">
            {loading && <p data-testid="admin-loading-message" className="text-[#B7BDCA]">Loading enquiries...</p>}
            {!loading && enquiries.length === 0 && <p data-testid="admin-empty-message" className="border border-[#2A2F3D] p-6 text-[#B7BDCA]">No enquiries yet.</p>}
            {enquiries.map((item, index) => (
              <article data-testid={`admin-enquiry-card-${index}`} key={item.id} className="grid gap-5 border border-[#2A2F3D] bg-[#12141A] p-6 lg:grid-cols-[1fr_220px]">
                <div>
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <h2 data-testid={`admin-enquiry-name-${index}`} className="font-display text-2xl font-semibold uppercase text-[#F3F4F6]">{item.name}</h2>
                    <span data-testid={`admin-enquiry-status-${index}`} className="border border-[#C5A059]/60 px-3 py-1 text-xs uppercase tracking-[0.16em] text-[#C5A059]">{item.status}</span>
                  </div>
                  <p data-testid={`admin-enquiry-service-${index}`} className="text-sm uppercase tracking-[0.18em] text-[#C5A059]">{item.service}</p>
                  <p data-testid={`admin-enquiry-message-${index}`} className="mt-4 text-base leading-7 text-[#D7DBE4]">{item.message}</p>
                  <div className="mt-5 grid gap-2 text-sm text-[#B7BDCA] sm:grid-cols-2">
                    <a data-testid={`admin-enquiry-phone-${index}`} href={`tel:${item.phone}`}>{item.phone}</a>
                    <a data-testid={`admin-enquiry-email-${index}`} href={`mailto:${item.email}`}>{item.email}</a>
                    <span data-testid={`admin-enquiry-date-${index}`}>{new Date(item.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div data-testid={`admin-enquiry-actions-${index}`} className="flex flex-col gap-3">
                  {['new', 'contacted', 'completed'].map((status) => (
                    <Button data-testid={`admin-status-${status}-button-${index}`} key={status} onClick={() => updateStatus(item.id, status)} className="rounded-none border border-[#2A2F3D] bg-[#0A0B0E] text-[#F3F4F6] hover:border-[#C5A059] hover:bg-[#151923]">Mark {status}</Button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      <Toaster richColors position="top-right" />
    </PageShell>
  );
}

export default App;
